import PatientDataAccessTab from "./PatientDataAccessTab";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography, CircularProgress, TextField, MenuItem, Chip } from "@mui/material";
import {
  Person,
  LocalHospital,
  Assignment,
  Event,
  MedicalServices,
  Check,
  Warning,
} from "@mui/icons-material";
import { colors, typography } from "../theme/designSystem";
import ReportSummaryCard, { summaryCardsRowSx } from "./Reports/ReportSummaryCard";
import { toLocalDateStr, shiftMonthsLocalDateStr } from "../utils/datetime";
import {
  patientOverviewService,
  PatientOverview as OverviewData,
} from "../services/patientOverviewService";

/** Mesma régua de inativo da Linha do tempo (RN-528a). */
const INACTIVE_DAYS = 180;

/** Período padrão dos indicadores (decisão do Hilton, 30/08/2026): últimos 3 meses até hoje. */
const DEFAULT_PAST_MONTHS = 3;

// Larguras fixas por coluna para que as linhas alinhem entre si. No celular tudo vira duas
// colunas, com o cabeçalho da linha (nome / supervisor) ocupando a faixa inteira.
const PROFESSIONAL_COLUMNS = {
  xs: "repeat(2, minmax(0, 1fr))",
  md: "minmax(160px, 1.6fr) 110px 130px 155px 105px 80px",
};


interface Props {
  patientId: string;
  /** Abas realmente funcionais — card só vira link para tela que existe de verdade. */
  onNavigate?: (tab: string) => void;
  navigableTabs?: string[];
}

interface StatusCard {
  key: string;
  icon: React.ElementType;
  iconColor: string;
  title: string;
  detail: string;
  ok: boolean;
  tab?: string;
}

const PatientOverview: React.FC<Props> = ({ patientId, onNavigate, navigableTabs = [] }) => {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [from, setFrom] = useState(() => shiftMonthsLocalDateStr(-DEFAULT_PAST_MONTHS));
  const [to, setTo] = useState(() => toLocalDateStr(new Date()));

  // A lista de profissionais do filtro vem da carga sem filtro: filtrar por um profissional não
  // pode fazer os outros sumirem do próprio seletor.
  const [allProfessionals, setAllProfessionals] = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await patientOverviewService.get(patientId, {
        professionalId: professionalId || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setData(result);
      setAllProfessionals((prev) =>
        prev.length === 0 ? result.professionals.map((p) => ({ id: p.id, name: p.name })) : prev
      );
    } catch {
      setError("Não foi possível carregar a visão geral do paciente.");
    } finally {
      setLoading(false);
    }
  }, [patientId, professionalId, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const cards: StatusCard[] = useMemo(() => {
    const s = data?.status;
    if (!s) return [];

    return [
      {
        key: "cadastro",
        icon: Person,
        iconColor: colors.primary,
        title: "Cadastro",
        detail: s.registrationComplete ? "Dados completos" : "Cadastro incompleto",
        ok: s.registrationComplete,
        tab: "cadastro",
      },
      {
        key: "diagnostico",
        icon: LocalHospital,
        iconColor: "#f44336",
        title: "Diagnóstico",
        detail:
          s.diagnosisCount > 0
            ? `${s.diagnosisCount} registro${s.diagnosisCount > 1 ? "s" : ""}${s.lastDiagnosisDate ? ` — último em ${s.lastDiagnosisDate}` : ""}`
            : "Sem diagnóstico registrado",
        ok: s.diagnosisCount > 0,
        tab: "diagnostico",
      },
      {
        key: "plano",
        icon: Assignment,
        iconColor: "#2196f3",
        title: "Plano terap.",
        detail: s.hasCurrentPlan
          ? `Plano vigente${s.currentPlanEndDate ? ` até ${s.currentPlanEndDate}` : " (sem prazo)"}`
          : s.planCount > 0
            ? `${s.planCount} plano${s.planCount > 1 ? "s" : ""}, nenhum vigente`
            : "Sem plano terapêutico",
        ok: s.hasCurrentPlan,
      },
      {
        key: "agendamento",
        icon: Event,
        iconColor: "#9c27b0",
        title: "Agendamento",
        detail: s.nextScheduleDate
          ? `${s.nextScheduleDate}${s.nextScheduleTime ? ` às ${s.nextScheduleTime}` : ""}${s.nextScheduleProfessional ? ` — ${s.nextScheduleProfessional}` : ""}`
          : "Sem agendamento futuro",
        ok: !!s.nextScheduleDate,
        tab: "agenda",
      },
      {
        key: "atendimentos",
        icon: MedicalServices,
        iconColor: "#4caf50",
        title: "Atendimentos",
        detail:
          s.lastAttendanceDate && s.daysSinceLastAttendance != null
            ? `Último em ${s.lastAttendanceDate} — há ${s.daysSinceLastAttendance} dia${s.daysSinceLastAttendance === 1 ? "" : "s"}`
            : "Nenhum atendimento realizado",
        // Ter atendido um dia não basta: parado há mais de 180 dias é a mesma régua de inativo da
        // Linha do tempo (RN-528a). Verde para quem não vem há dois anos seria mentira.
        ok: s.daysSinceLastAttendance != null && s.daysSinceLastAttendance < INACTIVE_DAYS,
        tab: "linha-do-tempo",
      },
    ];
  }, [data]);

  const a = data?.attendance;
  const cancelled = a ? a.cancelledByPatient + a.cancelledByProfessional + a.cancelledByClinic : 0;

  const slices = a
    ? [
        { label: "Compareceu", value: a.attended, color: colors.success },
        { label: "Faltas", value: a.noShows, color: colors.error },
        { label: "Cancelamentos", value: cancelled, color: colors.warning },
        { label: "Sem registro", value: a.withoutRecord, color: colors.textMuted },
      ].filter((sl) => sl.value > 0)
    : [];

  return (
    <div className="tab-content-section">

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ color: colors.error, fontSize: "0.9rem", py: 2 }}>{error}</Typography>
      )}

      {!loading && !error && data && (
        <>
          {/* Régua de situação. Laranja em tudo que falta: a lacuna de registro é para ser vista. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {cards.map((card) => {
              const Icon = card.icon;
              const clickable = !!card.tab && navigableTabs.includes(card.tab) && !!onNavigate;
              return (
                <Paper
                  key={card.key}
                  elevation={0}
                  onClick={clickable ? () => onNavigate!(card.tab!) : undefined}
                  sx={{
                    position: "relative",
                    p: 2,
                    border: `1px solid ${card.ok ? colors.border : colors.warning}`,
                    borderRadius: "8px",
                    cursor: clickable ? "pointer" : "default",
                    transition: "box-shadow .15s",
                    "&:hover": clickable ? { boxShadow: "0 2px 8px rgba(0,0,0,.10)" } : undefined,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      backgroundColor: card.ok ? colors.success : colors.warning,
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {card.ok ? (
                      <Check sx={{ fontSize: "0.875rem", color: "#fff" }} />
                    ) : (
                      <Warning sx={{ fontSize: "0.875rem", color: "#fff" }} />
                    )}
                  </Box>
                  <Icon sx={{ fontSize: "1.8rem", color: card.iconColor, mb: 0.5 }} />
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: colors.textPrimary }}>
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: colors.textSecondary, lineHeight: 1.4, pr: 2 }}
                  >
                    {card.detail}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {/* Indicadores de agenda — mesmos números da Linha do tempo (RN-528d/e). */}
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, mb: 2, color: colors.textPrimary }}>
              Presenças e perdas
            </Typography>

            {/* Escopo 'own' (RN-529d): os números já vêm restritos pelo servidor — o seletor
                aparece TRAVADO no profissional da sessão, sem "Todos". Administrador e
                Secretário (escopo de entidade) escolhem "Todos" ou quem atende o paciente. */}
            {data.scopedToOwnProfessional && (
              <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 2 }}>
                Mostrando <b>apenas os seus atendimentos</b> com este paciente.
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
              {data.scopedToOwnProfessional ? (
                <TextField
                  select
                  size="small"
                  label="Profissional"
                  value={data.ownProfessionalId ?? ""}
                  disabled
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 220, flex: "0 1 280px", backgroundColor: "#fff" }}
                >
                  <MenuItem value={data.ownProfessionalId ?? ""}>
                    {data.ownProfessionalName || "Você"}
                  </MenuItem>
                </TextField>
              ) : (
                <TextField
                  select
                  size="small"
                  label="Profissional"
                  value={professionalId}
                  onChange={(e) => setProfessionalId(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 220, flex: "0 1 280px", backgroundColor: "#fff" }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {allProfessionals.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                type="date"
                size="small"
                label="De"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: "0 1 190px", minWidth: 160, backgroundColor: "#fff" }}
              />
              <TextField
                type="date"
                size="small"
                label="Até"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: "0 1 190px", minWidth: 160, backgroundColor: "#fff" }}
              />
            </Box>

            {/* Donut à esquerda; indicadores nos cards de resumo canônicos (mesmos do Dashboard,
                relatórios e Linha do tempo). Quebra dos cancelamentos e "sem presença registrada"
                vivem dentro dos cards a que pertencem; o donut segue mostrando a composição. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
                columnGap: { md: 4 },
                rowGap: 2.5,
                alignItems: "center",
              }}
            >
              <Donut slices={slices} total={a?.scheduled ?? 0} />

              <Box sx={{ ...summaryCardsRowSx, mb: 0, minWidth: 0 }}>
                <ReportSummaryCard
                  label="Agendamentos"
                  value={String(a?.scheduled ?? 0)}
                  sub={(a?.withoutRecord ?? 0) > 0 ? `${a?.withoutRecord} sem presença registrada` : undefined}
                  subColor={colors.warning}
                />
                <ReportSummaryCard label="Compareceu" value={String(a?.attended ?? 0)} color={colors.success} />
                <ReportSummaryCard
                  label="Faltas"
                  value={String(a?.noShows ?? 0)}
                  color={(a?.noShows ?? 0) > 0 ? colors.error : undefined}
                />
                <ReportSummaryCard
                  label="Cancelamentos"
                  value={String(cancelled)}
                  color={colors.warning}
                  sub={`paciente ${a?.cancelledByPatient ?? 0} · profissional ${a?.cancelledByProfessional ?? 0} · feriado/recesso ${a?.cancelledByClinic ?? 0}`}
                />
                <ReportSummaryCard
                  label="Aproveitamento"
                  value={`${a?.attendanceRate ?? 0}%`}
                  color={(a?.attendanceRate ?? 0) < 70 ? colors.error : colors.success}
                  sub="presenças ÷ (presenças + faltas + canc. do paciente + sem registro)"
                  note={(a?.withoutRecord ?? 0) > 0 ? "sem registro conta como não aproveitado; futuro não entra" : "agendamento futuro não entra"}
                />
              </Box>
            </Box>
          </Paper>

          {/* Quem atende */}
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, mb: 2, color: colors.textPrimary }}>
              Quem atende o paciente
            </Typography>

            {data.professionals.length === 0 && (
              <Typography sx={{ color: colors.textSecondary, fontSize: "0.875rem" }}>
                Nenhum atendimento realizado até agora.
              </Typography>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {data.professionals.map((p) => (
                <Box
                  key={`${p.name}-${p.specialty}`}
                  sx={{
                    p: 1.5,
                    backgroundColor: colors.backgroundAlt,
                    borderRadius: "6px",
                    borderLeft: `3px solid ${p.active ? colors.primary : colors.border}`,
                    // Grid com as MESMAS larguras em todas as linhas: com flex, nome comprido
                    // empurrava as colunas e cada linha alinhava num ponto diferente.
                    display: "grid",
                    gridTemplateColumns: PROFESSIONAL_COLUMNS,
                    columnGap: 2,
                    rowGap: 1,
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: colors.textPrimary }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>
                      {p.specialty}
                    </Typography>
                  </Box>
                  {/* Sem base para calcular, o campo diz isso em vez de rotular um padrao
                      que ninguem observou -- mesma regra do aproveitamento. */}
                  <Field
                    label="Frequência"
                    value={p.cadence === 'poucas sessões' ? '—' : p.cadence}
                    sub={p.cadence === 'poucas sessões' ? 'poucas sessões para calcular' : undefined}
                  />
                  <Field label="Últ. atendimento" value={p.lastAttendance ?? "—"} />
                  <Field label="Próximo" value={p.nextSchedule ?? "—"} />
                  <Field label="90 dias" value={`${p.sessionsLast90Days} sessões`} />
                  <Box sx={{ justifySelf: { xs: "start", md: "end" } }}>
                    {!p.active && (
                      <Chip
                        size="small"
                        label="Inativo"
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          bgcolor: colors.white,
                          color: colors.textSecondary,
                          fontStyle: "italic",
                          border: `1px solid ${colors.border}`,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Acesso a dados (RN-540a). Era o bloco "Supervisores x Supervisionados", somente leitura,
              com a aba "Acesso" repetindo o mesmo dado no cadastro sob outro nome (item 51). Agora é
              um lugar só, com o editor: a mesma tabela, o vocabulário da regra. */}
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, mb: 2, color: colors.textPrimary }}>
              Quem tem acesso aos dados
            </Typography>
            <PatientDataAccessTab patientId={patientId} />
          </Paper>
        </>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.68rem", color: colors.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: colors.textPrimary }}>{value}</Typography>
    {sub && (
      <Typography sx={{ fontSize: "0.68rem", color: colors.textSecondary }}>{sub}</Typography>
    )}
  </Box>
);

/**
 * Donut da composiÃ§Ã£o dos agendamentos. O raio 15.915 faz a circunferÃªncia valer 100, entÃ£o cada
 * fatia Ã© a prÃ³pria porcentagem â€” sem conta de perÃ­metro.
 */
const Donut: React.FC<{ slices: { label: string; value: number; color: string }[]; total: number }> = ({
  slices,
  total,
}) => {
  const sum = slices.reduce((acc, s) => acc + s.value, 0);
  let offset = 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
      <Box sx={{ position: "relative", width: 120, height: 120, flexShrink: 0 }}>
        <svg width="120" height="120" viewBox="0 0 42 42" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#e9ecef" strokeWidth="3" />
          {sum > 0 &&
            slices.map((s) => {
              const pct = (100 * s.value) / sum;
              const dash = `${pct} ${100 - pct}`;
              const el = (
                <circle
                  key={s.label}
                  cx="21"
                  cy="21"
                  r="15.915"
                  fill="transparent"
                  stroke={s.color}
                  strokeWidth="3"
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                />
              );
              offset += pct;
              return el;
            })}
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: colors.primary, lineHeight: 1 }}>
            {total}
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: colors.textSecondary }}>Sessões</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {slices.length === 0 && (
          <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>Sem sessões no período.</Typography>
        )}
        {slices.map((s) => (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: s.color, borderRadius: "50%", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.8rem", color: colors.textPrimary }}>
              {s.label} <b>{s.value}</b>{" "}
              <span style={{ color: colors.textSecondary }}>({Math.round((100 * s.value) / sum)}%)</span>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PatientOverview;
